import {
  CircleCheckRegularIcon,
  CircleExclamationSolidIcon,
  HourglassHalfSolidIcon,
} from '@app/components/icons'
import {CustomToastOptions} from '@app/utils/toast'
import {
  Box,
  Button,
  Center,
  CloseButton,
  Flex,
  Progress,
  Stack,
  Text,
} from '@chakra-ui/react'
import {motion} from 'framer-motion'

import toast, {
  Toast as ToastOptions,
  ToastType,
  resolveValue,
} from 'react-hot-toast/headless'

const MotionBox = motion.create(Box)

export type ToastProps = ToastOptions & CustomToastOptions

export default function Toast({...opts}: ToastProps) {
  const variantProps = getVariantProps(opts.type)

  return (
    <MotionBox
      {...opts.ariaProps}
      animate={{
        opacity: 1,
        scale: 1,
        y: 0,
      }}
      boxShadow="0 2px 10px hsl(0 0% 0% / 0.25)"
      exit={{
        opacity: 0,
        scale: 0.5,
        transition: {
          duration: 0.2,
        },
      }}
      position="relative"
      fontSize="sm"
      initial={{
        opacity: 0,
        scale: 0.3,
        y: 50,
      }}
      style={{
        ...opts.style,
      }}
      backgroundColor="white"
      borderRadius="xl"
      padding="3"
      border="1px solid"
      borderColor="gray.200"
      width="80"
      maxWidth="100%"
    >
      <Flex
        gap="3"
        alignItems={opts.description || opts.action ? 'flex-start' : 'center'}
        paddingInlineEnd="4"
      >
        <Center
          border="1px solid"
          borderColor="gray.200"
          rounded="lg"
          boxSize="7"
          minWidth="7"
          boxShadow="sm"
          backgroundColor={`${variantProps?.colorScheme}.600`}
          color="white"
        >
          {variantProps?.icon || <CircleCheckRegularIcon boxSize="5" />}
        </Center>
        <Stack fontSize="sm" gap="1" letterSpacing="tight" flex="1">
          <Text fontWeight="bold">{resolveValue(opts.message, opts)}</Text>
          {opts.description && (
            <Text
              fontSize="13px"
              lineHeight="18px"
              color="gray.600"
              lineClamp={3}
            >
              {opts.description}
            </Text>
          )}

          {opts.type === 'loading' && (
            <Stack gap="2" alignItems="end">
              <Progress.Root
                colorPalette="brand"
                size="sm"
                rounded="md"
                width="full"
                marginBlockStart="3"
                value={opts.progress ?? null}
              >
                <Progress.Track>
                  <Progress.Range />
                </Progress.Track>
              </Progress.Root>
              <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                {Math.floor(opts.progress ?? 0).toString() + '%'}
              </Text>
            </Stack>
          )}
          {opts.action && (
            <Button
              marginBlockStart="1"
              alignSelf="start"
              size="xs"
              height="6"
              minWidth="auto"
              padding="0"
              borderWidth="0"
              variant="plain"
              onClick={() => {
                opts.action?.onClick()
                toast.remove(opts.id)
              }}
              colorPalette={variantProps?.colorScheme || 'blue'}
            >
              {opts.action.label}
            </Button>
          )}
        </Stack>
        <CloseButton
          position="absolute"
          insetInlineEnd="2"
          insetBlockStart="2"
          marginInlineStart="auto"
          alignSelf={opts.description || opts.action ? 'flex-start' : 'center'}
          color="gray.600"
          fontSize="10px"
          boxSize="5"
          minWidth="5"
          padding="0"
          aria-label="Cerrar aviso"
          onClick={() => toast.remove(opts.id)}
        />
      </Flex>
    </MotionBox>
  )
}

function getVariantProps(type: ToastType) {
  switch (type) {
    case 'loading':
      return {
        icon: <HourglassHalfSolidIcon />,
        colorScheme: 'orange',
      }
    case 'success':
      return {icon: <CircleCheckRegularIcon />, colorScheme: 'teal'}
    case 'error':
      return {icon: <CircleExclamationSolidIcon />, colorScheme: 'red'}
    default:
      return null
  }
}
